import { Inject, Injectable } from '@angular/core';

import { MoneyService } from '@frontend/sports/common/core/feature/money';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';
import { Store } from '@ngrx/store';
import { Decimal } from 'decimal.js';
import { Observable, map, of, switchMap, take } from 'rxjs';

import { IBetslipMessages } from '../../../../../base/models/betslip-messages';
import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    IBetslipMessagesProviderService,
} from '../../../../../base/services/betslip-messages-provider.service';
import { IBetslipRootState } from '../../../../../base/store/state';
import { BetslipType } from '../../../../../core/betslip-type';
import { singleBetStateSelector } from '../../../../single-bet/selectors';
import { betslipTypeStateSelector } from '../../../../types/selectors';
import { BetslipError } from '../../../errors/betslip-error';
import { OverMaximumStakePreCheckError } from '../../../errors/pre-check/over-maximum-stake-pre-check-error';
import { MessageFormatter } from './message-formatter';

@Injectable()
export class OverMaximumStakeMessageFormatter extends MessageFormatter<OverMaximumStakePreCheckError> {
    private messages: IBetslipMessages;
    constructor(
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: IBetslipMessagesProviderService,
        private stringInterpolationPipe: StringInterpolationPipe,
        private moneyService: MoneyService,
        private store: Store<IBetslipRootState>,
    ) {
        super();
        this.messages = messageProvider.getMessages();
    }

    format(error: OverMaximumStakePreCheckError): Observable<string> {
        const amount = new Decimal(error.maximumStake).div(error.numberOfBetsInBet).toDecimalPlaces(2, Decimal.ROUND_DOWN);

        const obj = {
            amountWithCurrency: this.moneyService.transform(amount, true),
        };

        return this.store.select(betslipTypeStateSelector).pipe(
            switchMap((type) => {
                switch (type.base.currentSelectedType) {
                    case BetslipType.Combo:
                    case BetslipType.EditBet:
                    case BetslipType.Teaser:
                    case BetslipType.System:
                        return of(this.stringInterpolationPipe.transform(this.messages.validation.MaxStakeValue, obj));
                    case BetslipType.Single:
                        return this.store
                            .select(singleBetStateSelector)
                            .pipe(
                                map((singleBet) =>
                                    Object.keys(singleBet.picks).length === 1
                                        ? this.stringInterpolationPipe.transform(this.messages.validation.MaxStakeValue, obj)
                                        : this.stringInterpolationPipe.transform(this.messages.validation.MaxStakePerBet, obj),
                                ),
                            );
                    default:
                        return of('');
                }
            }),
            take(1),
        );
    }

    canHandle(error: BetslipError): boolean {
        return error instanceof OverMaximumStakePreCheckError;
    }
}
